import { useState, useEffect, useCallback } from 'react'
import { ITANI_NETWORK } from '../lib/blockchain'

const getPreferredNetworkTarget = () => {
  const target = String(
    import.meta.env.VITE_ITANI_NETWORK_TARGET ||
      import.meta.env.VITE_NETWORK_TARGET ||
      'mainnet'
  )
    .trim()
    .toLowerCase()

  return target === 'testnet' ? 'testnet' : 'mainnet'
}

const getTargetNetwork = (target = 'mainnet') =>
  target === 'testnet' ? ITANI_NETWORK.testnet : ITANI_NETWORK

const isSupportedChainId = (chainId) =>
  chainId === ITANI_NETWORK.testnet.chainId || chainId === ITANI_NETWORK.chainId

const getChainName = (chainId) => {
  if (chainId === ITANI_NETWORK.testnet.chainId) {
    return ITANI_NETWORK.testnet.name
  }

  if (chainId === ITANI_NETWORK.chainId) {
    return ITANI_NETWORK.name
  }

  if (chainId) {
    return `Connected (${chainId})`
  }

  return null
}

export const useMetaMask = () => {
  const [account, setAccount] = useState(null)
  const [chainId, setChainId] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState(null)

  const ITANI_CHAIN_ID = ITANI_NETWORK.chainId
  const ITANI_TESTNET_CHAIN_ID = ITANI_NETWORK.testnet.chainId

  const switchNetwork = useCallback(async (target = 'mainnet') => {
    if (!window.ethereum) {
      setError('MetaMask not found')
      return false
    }

    const network = getTargetNetwork(target)

    try {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: network.chainId,
          chainName: network.name,
          nativeCurrency: ITANI_NETWORK.token,
          rpcUrls: [network.rpc],
          blockExplorerUrls: [network.explorer],
        }],
      })
    } catch (e) {
      if (e.code !== 4001) console.warn('Network add failed:', e)
      if (e.code === 4001) {
        setError('Network switch cancelled')
        return false
      }
    }

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: network.chainId }],
      })
      setChainId(network.chainId)
      setError(null)
      return true
    } catch (e) {
      if (e.code !== 4001) console.warn('Network switch failed:', e)
      setError(e.message || 'Unable to switch network')
      return false
    }
  }, [])

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setError('MetaMask not found')
      return
    }

    setIsConnecting(true)
    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      })
      const currentChainId = await window.ethereum.request({
        method: 'eth_chainId',
      })

      setAccount(accounts[0])
      setChainId(currentChainId)

      const preferredTarget = getPreferredNetworkTarget()
      const preferredNetwork = getTargetNetwork(preferredTarget)

      if (!isSupportedChainId(currentChainId) || currentChainId !== preferredNetwork.chainId) {
        const switched = await switchNetwork(preferredTarget)
        if (!switched) {
          setError(`Please switch to ${preferredNetwork.name}`)
          return
        }
      } else {
        setError(null)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setIsConnecting(false)
    }
  }, [switchNetwork])

  const disconnect = useCallback(() => {
    setAccount(null)
    setChainId(null)
  }, [])

  // Listen for account changes
  useEffect(() => {
    if (!window.ethereum) return

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnect()
      } else {
        setAccount(accounts[0])
      }
    }

    const handleChainChanged = (chain) => {
      setChainId(chain)
      if (isSupportedChainId(chain)) {
        setError(null)
      } else {
        setError('Unsupported network')
      }
    }

    window.ethereum.on('accountsChanged', handleAccountsChanged)
    window.ethereum.on('chainChanged', handleChainChanged)

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
      window.ethereum.removeListener('chainChanged', handleChainChanged)
    }
  }, [disconnect])

  return {
    account,
    chainId,
    chainName: getChainName(chainId),
    isConnecting,
    error,
    isTestnet: chainId === ITANI_TESTNET_CHAIN_ID,
    isMainnet: chainId === ITANI_CHAIN_ID,
    connect,
    disconnect,
    switchNetwork,
    switchToMainnet: () => switchNetwork('mainnet'),
    switchToTestnet: () => switchNetwork('testnet'),
  }
}

export default useMetaMask
